import { cn } from "~/lib/utils";
import { useState, useEffect } from "react";
import type { LottieComponentProps } from "lottie-react";

const ANIMATION_URLS = {
  birby: "https://asset.blidz.com/blidz-cdn/web/landing-page/birby-web.json",
  topLeft: "https://asset.blidz.com/blidz-cdn/web/landing-page/landing-page-top-left-v2.json",
  topRight: "https://asset.blidz.com/blidz-cdn/web/landing-page/landing-page-top-right-v2.json",
  centerLeft: "https://asset.blidz.com/blidz-cdn/web/landing-page/landing-page-web-left-v2.json",
  centerRight: "https://asset.blidz.com/blidz-cdn/web/landing-page/landing-page-web-right-v2.json",
};

function useLottie() {
  const [LottieComponent, setLottieComponent] = useState<React.ComponentType<LottieComponentProps> | null>(null);

  useEffect(() => {
    // Dynamic import to avoid SSR issues
    import("lottie-react").then(module => {
      setLottieComponent(() => module.default);
    });
  }, []);

  return LottieComponent;
}

function useAnimationData(url: string) {
  const [animationData, setAnimationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnimation = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch animation: ${response.status}`);
        }
        const data = await response.json();
        setAnimationData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load animation");
        console.error("Error fetching animation:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnimation();
  }, [url]);

  return { animationData, loading, error };
}

export function LandingBirbyAnimation({ className }: { className?: string }) {
  const LottieComponent = useLottie();
  const { animationData, loading, error } = useAnimationData(ANIMATION_URLS.birby);

  return (
    <div className={cn("h-full w-full", className)}>
      {error || loading || !LottieComponent || !animationData ? (
        <div className="h-full w-full" />
      ) : (
        <LottieComponent animationData={animationData} loop={false} />
      )}
    </div>
  );
}

export function LandingTopAnimationLeft({ className }: { className?: string }) {
  const LottieComponent = useLottie();
  const { animationData, loading, error } = useAnimationData(ANIMATION_URLS.topLeft);

  return (
    <div className={cn("h-full w-full", className)}>
      {error || loading || !LottieComponent || !animationData ? (
        <div className="h-full w-full" />
      ) : (
        <LottieComponent animationData={animationData} loop={false} />
      )}
    </div>
  );
}

export function LandingTopAnimationRight({ className }: { className?: string }) {
  const LottieComponent = useLottie();
  const { animationData, loading, error } = useAnimationData(ANIMATION_URLS.topRight);

  return (
    <div className={cn("h-full w-full", className)}>
      {error || loading || !LottieComponent || !animationData ? (
        <div className="h-full w-full" />
      ) : (
        <LottieComponent animationData={animationData} loop={false} />
      )}
    </div>
  );
}

export function LandingCenterAnimationLeft({ className }: { className?: string }) {
  const LottieComponent = useLottie();
  const { animationData, loading, error } = useAnimationData(ANIMATION_URLS.centerLeft);

  return (
    <div className={cn("h-full w-full", className)}>
      {error || loading || !LottieComponent || !animationData ? (
        <div className="h-full w-full" />
      ) : (
        <LottieComponent animationData={animationData} loop={true} />
      )}
    </div>
  );
}

export function LandingCenterAnimationRight({ className }: { className?: string }) {
  const LottieComponent = useLottie();
  const { animationData, loading, error } = useAnimationData(ANIMATION_URLS.centerRight);

  return (
    <div className={cn("h-full w-full", className)}>
      {error || loading || !LottieComponent || !animationData ? (
        <div className="h-full w-full" />
      ) : (
        <LottieComponent animationData={animationData} loop={true} />
      )}
    </div>
  );
}
